import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useLeadForm } from '../../../contexts/LeadFormContext';

const ProjectModal = ({ project, isOpen, onClose, onShare }) => {
  const { openLeadForm } = useLeadForm();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !project) return null;

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? project?.gallery?.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === project?.gallery?.length - 1 ? 0 : prev + 1
    );
  };

  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
  };

  const handleShare = () => {
    onShare(project);
  };

  // handleWhatsAppContact removido - usando contexto de lead form

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal Content */}
      <div className="relative bg-card rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-premium-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="font-playfair font-bold text-2xl text-text-primary">
              {project?.title}
            </h2>
            <p className="text-text-secondary">{project?.location}</p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="icon"
              iconName="Share2"
              onClick={handleShare}
              className="text-text-secondary hover:text-primary"
            />
            <Button
              variant="ghost"
              size="icon"
              iconName="X"
              onClick={onClose}
              className="text-text-secondary hover:text-primary"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col lg:flex-row max-h-[calc(90vh-80px)]">
          {/* Image Gallery */}
          <div className="lg:w-2/3 relative">
            <div className="relative h-96 lg:h-full">
              <Image
                src={project?.gallery?.[currentImageIndex]}
                alt={`${project?.title} - Imagem ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
              />
              
              {/* Navigation Arrows */}
              {project?.gallery?.length > 1 && (
                <>
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors duration-200"
                  >
                    <Icon name="ChevronLeft" size={20} color="white" />
                  </button>
                  <button
                    onClick={handleNextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors duration-200"
                  >
                    <Icon name="ChevronRight" size={20} color="white" />
                  </button>
                </>
              )}

              {/* Image Counter */}
              <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/50 text-white text-sm rounded-full">
                {currentImageIndex + 1} / {project?.gallery?.length}
              </div>
            </div>

            {/* Thumbnail Strip */}
            <div className="p-4 border-t border-border">
              <div className="flex space-x-2 overflow-x-auto">
                {project?.gallery?.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                      index === currentImageIndex
                        ? 'border-primary shadow-forest'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <Image
                      src={image}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Project Details */}
          <div className="lg:w-1/3 p-6 overflow-y-auto">
            {/* Project Stats */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="text-center p-3 bg-muted rounded-lg">
                <Icon name="Calendar" size={20} color="var(--color-primary)" className="mx-auto mb-1" />
                <p className="text-xs text-text-secondary">Concluído em</p>
                <p className="font-semibold text-text-primary">{project?.completionDate}</p>
              </div>
              <div className="text-center p-3 bg-muted rounded-lg">
                <Icon name="Clock" size={20} color="var(--color-primary)" className="mx-auto mb-1" />
                <p className="text-xs text-text-secondary">Duração</p>
                <p className="font-semibold text-text-primary">{project?.duration}</p>
              </div>
              <div className="text-center p-3 bg-muted rounded-lg">
                <Icon name="Package" size={20} color="var(--color-primary)" className="mx-auto mb-1" />
                <p className="text-xs text-text-secondary">Peças</p>
                <p className="font-semibold text-text-primary">{project?.pieceCount}</p>
              </div>
              <div className="text-center p-3 bg-muted rounded-lg">
                <Icon name="Star" size={20} color="var(--color-accent)" className="mx-auto mb-1" />
                <p className="text-xs text-text-secondary">Avaliação</p>
                <p className="font-semibold text-text-primary">{project?.rating}/5</p>
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <h4 className="font-semibold text-text-primary mb-2">Sobre o Projeto</h4>
              <p className="text-text-secondary text-sm leading-relaxed">
                {project?.fullDescription}
              </p>
            </div>

            {/* Client Testimonial */}
            <div className="mb-6 p-4 bg-muted rounded-lg">
              <div className="flex items-center space-x-3 mb-3">
                <Image
                  src={project?.client?.avatar}
                  alt={project?.client?.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="font-medium text-text-primary">{project?.client?.name}</p>
                  <p className="text-text-secondary text-xs">{project?.client?.title}</p>
                </div>
              </div>
              <blockquote className="text-text-secondary text-sm italic">
                "{project?.testimonial}"
              </blockquote>
            </div>

            {/* Video Testimonial */}
            {project?.videoTestimonial && (
              <div className="mb-6">
                <h4 className="font-semibold text-text-primary mb-2">Depoimento em Vídeo</h4>
                <div className="relative rounded-lg overflow-hidden">
                  {!isVideoPlaying ? (
                    <div className="relative">
                      <Image
                        src={project?.videoThumbnail}
                        alt="Video thumbnail"
                        className="w-full h-32 object-cover"
                      />
                      <button
                        onClick={handleVideoPlay}
                        className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors duration-200"
                      >
                        <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                          <Icon name="Play" size={20} color="white" />
                        </div>
                      </button>
                    </div>
                  ) : (
                    <video
                      src={project?.videoTestimonial}
                      controls
                      autoPlay
                      className="w-full h-32 object-cover"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Timeline */}
            <div className="mb-6">
              <h4 className="font-semibold text-text-primary mb-3">Timeline do Projeto</h4>
              <div className="space-y-3">
                {project?.timeline?.map((phase, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-text-primary text-sm">{phase?.phase}</p>
                      <p className="text-text-secondary text-xs">{phase?.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Related Products */}
            <div className="mb-6">
              <h4 className="font-semibold text-text-primary mb-3">Produtos Similares</h4>
              <div className="grid grid-cols-2 gap-2">
                {project?.relatedProducts?.map((product, index) => (
                  <div key={index} className="bg-muted rounded-lg p-2">
                    <Image
                      src={product?.image}
                      alt={product?.name}
                      className="w-full h-16 object-cover rounded mb-1"
                    />
                    <p className="text-xs font-medium text-text-primary">{product?.name}</p>
                    <p className="text-xs text-text-secondary">{product?.category}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                variant="default"
                fullWidth
                iconName="MessageCircle"
                iconPosition="left"
                onClick={openLeadForm}
                className="bg-gradient-forest hover:shadow-forest"
              >
                Conversar no WhatsApp
              </Button>
              <Button
                variant="outline"
                fullWidth
                iconName="Calendar"
                iconPosition="left"
                className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
              >
                Agendar Consultoria
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;